<template>
  <div class="aiw2-stepper">
    <template v-for="(step, idx) in visibleSteps">
      <button
        :key="step"
        class="step"
        :class="{
          active: step === currentStep,
          done: isDone(step),
          locked: isLocked(step),
          'has-lock': frozen || (setupLocked && step === 'setup'),
          frozen,
        }"
        :disabled="isLocked(step)"
        @click="onClick(step)"
      >
        <span class="num"><span v-if="!isDone(step)">{{ displayNumber(step) }}</span></span>
        {{ labels[step] }}
      </button>
      <span v-if="idx < visibleSteps.length - 1" :key="step + '-arrow'" class="arrow">›</span>
    </template>
  </div>
</template>

<script>
import { STEPS, STEP_LABELS, MODES } from "../constants";

export default {
  name: "AiwV2Stepper",
  props: {
    currentStep: { type: String, default: "mode" },
    mode: { type: String, default: MODES.GUIDED },
    // steps reachable so far (others render as locked)
    reachableSteps: { type: Array, default: () => ["mode", "setup"] },
    // Publish is terminal: chips stay visible (and green) but aren't clickable.
    // Kept separate from `locked`, which means "not yet started" and dims the chip.
    frozen: { type: Boolean, default: false },
    // Setup is a one-way lock once the user continues past it — its chip becomes
    // non-clickable (like a not-yet-reached step).
    setupLocked: { type: Boolean, default: false },
  },
  data() {
    return { labels: STEP_LABELS };
  },
  computed: {
    visibleSteps() {
      // "Mode" is the entry screen, not a numbered step (design parity); Fast
      // Draft also hides the Title step (mirrors prototype data-step-title).
      return STEPS.filter((s) => s !== "mode" && !(s === "title" && this.mode === MODES.FAST));
    },
  },
  methods: {
    displayNumber(step) {
      return this.visibleSteps.indexOf(step) + 1;
    },
    indexOf(step) {
      return this.visibleSteps.indexOf(step);
    },
    isDone(step) {
      return this.indexOf(step) < this.indexOf(this.currentStep);
    },
    isLocked(step) {
      if (this.setupLocked && step === "setup") return true;
      return !this.reachableSteps.includes(step);
    },
    onClick(step) {
      if (step === this.currentStep) return;
      // Once the article is final (frozen), earlier chips aren't navigable —
      // surface "Your article is final." instead of moving.
      if (this.frozen) { this.$emit("locked-click"); return; }
      if (this.isLocked(step)) return;
      this.$emit("go", step);
    },
  },
};
</script>

<style scoped>
.aiw2-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  background: var(--surface, #fff);
  border-bottom: 1px solid var(--border, #e4e4e4);
  font-family: "Mulish", system-ui, sans-serif;
  flex-wrap: wrap;
}
.step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.82rem;
  color: var(--text-3, #6b7b7e);
  cursor: pointer;
  padding: 4px 2px;
}
.step.active { color: var(--text, #052e33); }
.step.locked { cursor: not-allowed; opacity: 0.55; }
/* Padlock after the label (design handoff: .step.locked::after): the one-way
   Setup lock, plus every step once the run is frozen on Publish — except the
   current step itself. Not shown on plain not-yet-reached steps (also `.locked`). */
.step.has-lock:not(.active)::after { content: "\1F512"; font-size: 10px; margin-left: 5px; opacity: 0.8; }
/* Frozen (Publish reached): the run is finalized, so the earlier steps show the
   locked design — dimmed + padlock — while the current Publish step stays normal. */
.step.frozen { cursor: default; }
.step.frozen:not(.active) { cursor: not-allowed; opacity: 0.55; }
.num {
  width: 26px; height: 26px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--surface, #fff);
  border: 1.5px solid var(--border-strong, #ccc);
  color: var(--text-3, #6b7b7e);
  font-size: 0.78rem;
}
.step.active .num { border-color: var(--pop-orange, #ff5700); color: var(--pop-orange, #ff5700); }
.step.done .num {
  background: var(--pop-green, #25a97a);
  border-color: var(--pop-green, #25a97a);
  color: #fff;
}
.step.done .num::after { content: "✓"; }
.arrow { color: var(--border-strong, #ccc); }
</style>
